import { fetchWritingData } from './dataApi';

// Writing is a catalog of worked example documents (letters/emails/
// applications/resume-or-cv — see writing/catalog.json), each a single
// English-anchored root file at writing/{category}/{filename} wrapped as
// { [docType]: {...} } (docType matches the catalog entry's `type`, e.g.
// "letter"/"email"/"application"/"cv"), plus a
// writing/{category}/native/{code}/{filename} translation overlay. Every
// text leaf is authored as { txt, translate, textToSpeech } (same
// convention as topic-lessons/conversation), so sections merge/unwrap
// generically regardless of a document's own nested shape (a letter's
// greeting/opening/mainMessage/closing vs. a CV's personalDetails/
// education/experience/...) — see mergeDocumentOverlay / unwrapValue.

let catalogPromise = null;

function fetchCatalog() {
  if (!catalogPromise) {
    catalogPromise = fetchWritingData('catalog.json').catch((error) => {
      catalogPromise = null;
      throw error;
    });
  }
  return catalogPromise;
}

// The catalog's entries (id/type/category/label/path/emoji), in index
// order — callers group by `category` for the chip picker.
export async function loadWritingCatalog() {
  const catalog = await fetchCatalog();
  return Object.keys(catalog)
    .filter((key) => /^index\d+$/.test(key))
    .sort((a, b) => Number(a.slice(5)) - Number(b.slice(5)))
    .map((key) => catalog[key]);
}

function mergeValue(root, overlay) {
  if (overlay === null || overlay === undefined) return root;
  if (Array.isArray(root) && Array.isArray(overlay)) {
    return root.map((item, i) => (i < overlay.length ? mergeValue(item, overlay[i]) : item));
  }
  if (root && overlay && typeof root === 'object' && typeof overlay === 'object' && !Array.isArray(root)) {
    const merged = { ...root };
    Object.entries(overlay).forEach(([key, value]) => {
      merged[key] = mergeValue(root[key], value);
    });
    return merged;
  }
  return overlay;
}

function unwrapValue(value) {
  if (Array.isArray(value)) return value.map(unwrapValue);
  if (value && typeof value === 'object') {
    if ('txt' in value) return value.txt;
    return Object.fromEntries(Object.entries(value).map(([key, v]) => [key, unwrapValue(v)]));
  }
  return value;
}

async function fetchDocContent(path) {
  const doc = await fetchWritingData(path);
  const values = Object.values(doc);
  return values.length > 0 ? values[0] : null;
}

// One document, merged with `locale`'s native overlay for display — falls
// back to the English root's own text wherever `locale` (or its overlay
// fetch) doesn't cover a field. Passing locale: 'en' (or falsy) returns the
// root as-is.
export async function loadWritingDocument(path, locale) {
  const root = await fetchDocContent(path);
  if (!root) throw new Error(`Unknown writing document: ${path}`);
  if (!locale || locale === 'en') return unwrapValue(root);

  const slash = path.indexOf('/');
  const category = path.slice(0, slash);
  const filename = path.slice(slash + 1);
  const overlay = await fetchDocContent(`${category}/native/${locale}/${filename}`).catch(() => null);
  return unwrapValue(mergeValue(root, overlay));
}

export function clearWritingV2Cache() {
  catalogPromise = null;
}
